import { DEFAULT_TENANT_THEME } from '@shopx-distribution/shared';
import { Outlet } from 'react-router-dom';

import { useMe } from '@/api/hooks.js';
import { DebugPanel } from '@/components/DebugPanel.js';
import { cn } from '@/lib/utils.js';

/**
 * One shell for both route trees. The Experience and Admin Console pages are
 * registered as separate base URLs in Kadanza but ship from the same Pages
 * project, so they share layout, theme and API client.
 *
 * There is deliberately no navigation or header bar here: Kadanza renders the
 * registered pages in its own sidebar and chrome, so a banner inside the iframe
 * would read as a second, competing header. The title is set at the same weight
 * as a host page title so the iframe reads as part of the host page.
 *
 * `scheme` decides where the palette comes from. The Experience tree takes it
 * from the tenant, because that page sits inside the host's own UI and some
 * realms render dark; the Admin Console is pinned light, matching Kadanza's
 * admin chrome everywhere.
 *
 * Until `/api/me` answers, the tenant's scheme is unknown and the default is
 * used. A dark tenant therefore opens light for the length of one request — the
 * alternative, blocking the whole page on `me`, trades a brief flash for a
 * blank screen on every navigation.
 */
export function AppShell({
  title,
  scheme = 'light',
}: {
  title: string;
  scheme?: 'light' | 'tenant';
}) {
  const me = useMe();
  const theme = scheme === 'tenant' ? (me.data?.tenant.theme ?? DEFAULT_TENANT_THEME) : 'light';

  return (
    <div
      className={cn('min-h-screen bg-page font-sans text-ink', theme === 'dark' && 'theme-dark')}
    >
      <main className="mx-auto max-w-6xl px-6 py-10">
        <h1 className="mb-6 text-3xl font-bold tracking-tight">{title}</h1>
        <Outlet />
      </main>

      <DebugPanel />
    </div>
  );
}
